// 日志终端顶部三 tab：System / Plan A / Plan B。Single 模式隐藏 Plan B。
// Log terminal tab strip (system / planA / planB); planB hidden in single mode.

import type { Mode } from "@/lib/types";
import type { TabState, TerminalTab } from "@/features/log-terminal/terminal-types";

interface Options {
  initialMode: Mode;
  onChange: (tab: TerminalTab) => void;
}

export interface TerminalTabsHandle {
  root: HTMLElement;
  setMode: (mode: Mode) => void;
  setActive: (tab: TerminalTab) => void;
  setUnread: (tab: TerminalTab, unread: boolean) => void;
  setLamp: (tab: TerminalTab, lamp: TabState["lamp"]) => void;
}

// tab 标签静态常量 / Static, safe-to-innerHTML labels.
const LABELS: Record<TerminalTab, string> = {
  system: "System",
  planA: "Plan A",
  planB: "Plan B",
};

// 创建一组 log terminal tabs / Build a tab strip handle.
// 每次调用返回独立实例；无模块级状态。
// Each call returns an independent instance; no module-level state.
export function createTerminalTabs(opts: Options): TerminalTabsHandle {
  const root = document.createElement("div");
  root.className = "terminal-tab-strip";
  root.setAttribute("role", "tablist");

  const state = {
    mode: opts.initialMode,
    active: "system" as TerminalTab,
    lamps: { system: "idle", planA: "idle", planB: "idle" } as Record<TerminalTab, TabState["lamp"]>,
    unread: { system: false, planA: false, planB: false } as Record<TerminalTab, boolean>,
  };

  // 全量重建 tab 按钮 / Full rebuild keeps listener/state logic trivial.
  const render = () => {
    const tabs: TerminalTab[] =
      state.mode === "ab" ? ["system", "planA", "planB"] : ["system", "planA"];
    root.replaceChildren();
    for (const tab of tabs) {
      const btn = buildTab(tab);
      btn.classList.toggle("active", tab === state.active);
      btn.setAttribute("aria-selected", String(tab === state.active));
      const lampEl = btn.querySelector<HTMLElement>(".terminal-tab-lamp");
      if (lampEl) lampEl.className = `terminal-tab-lamp ${state.lamps[tab]}`;
      if (state.unread[tab]) btn.classList.add("has-unread");
      btn.addEventListener("click", () => {
        state.active = tab;
        state.unread[tab] = false;
        opts.onChange(tab);
        render();
      });
      root.append(btn);
    }
  };

  render();

  return {
    root,
    setMode(mode) {
      state.mode = mode;
      // single 模式下 Plan B 消失，若当前激活 planB 则回退到 planA
      // Fallback active tab when planB disappears under single mode.
      if (mode === "single" && state.active === "planB") state.active = "planA";
      render();
    },
    setActive(tab) {
      state.active = tab;
      state.unread[tab] = false;
      render();
    },
    setUnread(tab, unread) {
      state.unread[tab] = unread;
      render();
    },
    setLamp(tab, lamp) {
      state.lamps[tab] = lamp;
      render();
    },
  };
}

// 构造单个 tab button（结构：灯 + 文本 + 未读点）
// Build a single tab button (lamp + label + unread dot).
function buildTab(tab: TerminalTab): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "terminal-tab";
  btn.dataset["tab"] = tab;
  btn.setAttribute("role", "tab");
  btn.innerHTML = `
    <span class="terminal-tab-lamp idle" aria-hidden="true"></span>
    <span class="terminal-tab-label">${LABELS[tab]}</span>
    <span class="terminal-tab-dot" aria-hidden="true"></span>
  `;
  return btn;
}
